import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mail, CheckCircle2, AlertTriangle, Save, Instagram, Linkedin, Facebook, Share2, Users, Upload } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import { PipelineFiltersCard } from "@/components/admin/PipelineFiltersCard";

const inputCls = "rounded-xl h-11";
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function absolutePhotoUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_BASE}${path}`;
}

export default function AdminSettings() {
  const { admin: me } = useAuth();
  const [settings, setSettings] = useState({
    notification_email: "",
    sender_email: "onboarding@resend.dev",
    sender_name: "Vanguard Beacon",
    social_instagram: "",
    social_linkedin: "",
    social_facebook: "",
    founder_john_photo_url: "",
    founder_chris_photo_url: "",
    resend_configured: false,
    updated_at: null,
    updated_by: null,
  });
  const [form, setForm] = useState({
    notification_email: "",
    sender_email: "",
    sender_name: "",
  });
  const [socialForm, setSocialForm] = useState({
    social_instagram: "",
    social_linkedin: "",
    social_facebook: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);
  const [uploadingFounder, setUploadingFounder] = useState(null); // "john" | "chris" | null

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setForm({
        notification_email: data.notification_email || "",
        sender_email: data.sender_email || "",
        sender_name: data.sender_name || "",
      });
      setSocialForm({
        social_instagram: data.social_instagram || "",
        social_linkedin: data.social_linkedin || "",
        social_facebook: data.social_facebook || "",
      });
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (me && me.role === "admin") fetchSettings();
  }, [fetchSettings, me]);

  if (me && me.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", form);
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onSaveSocial = async (e) => {
    e.preventDefault();
    setSavingSocial(true);
    try {
      const { data } = await api.patch("/admin/settings", socialForm);
      setSettings(data);
      toast.success("Social links updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSavingSocial(false);
    }
  };

  const onFounderPhotoChange = async (who, e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting the same file re-fires
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploadingFounder(who);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/settings/founder-photo/${who}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings((s) => ({ ...s, [`founder_${who}_photo_url`]: data.photo_url }));
      toast.success(`${who === "john" ? "John" : "Chris"}'s photo updated`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to upload photo");
    } finally {
      setUploadingFounder(null);
    }
  };

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const updateSocial = (k, v) => setSocialForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-settings-page">
      <AdminHeader />

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <p className="text-[11px] md:text-[13px] uppercase tracking-[0.45em] text-brand-orange font-semibold">Settings</p>
          <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-settings-heading">
            Configuration
          </h1>
          <p className="mt-2 text-sm text-brand-slate/75">
            Control where lead notification emails land and how outgoing emails are branded.
          </p>
        </div>

        {/* Email notifications card */}
        <section className="bg-white border border-brand-navy/10 rounded-2xl p-6 md:p-8" data-testid="settings-email-card">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="inline-flex w-10 h-10 items-center justify-center bg-brand-navy text-brand-orange rounded-full">
                <Mail size={18} />
              </span>
              <div>
                <h2 className="font-semibold text-brand-navy">Email notifications</h2>
                <p className="text-xs text-brand-slate/70">Sent via Resend whenever a new lead submits the enquiry form.</p>
              </div>
            </div>
            {settings.resend_configured ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 rounded-full" data-testid="resend-status-ok">
                <CheckCircle2 size={12} className="mr-1" /> Resend connected
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 rounded-full" data-testid="resend-status-missing">
                <AlertTriangle size={12} className="mr-1" /> API key missing
              </Badge>
            )}
          </div>

          <form onSubmit={onSave} className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="notification_email" className="text-sm text-brand-navy">Notification email (recipient)</Label>
              <Input
                id="notification_email"
                type="email"
                value={form.notification_email}
                onChange={(e) => update("notification_email", e.target.value)}
                className={inputCls}
                required
                disabled={loading}
                data-testid="settings-input-notification-email"
              />
              <span className="text-[11px] text-brand-slate/60">
                Where every new lead notification gets delivered.
                {!settings.resend_configured && " You'll need to set RESEND_API_KEY in backend env first."}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sender_email" className="text-sm text-brand-navy">Sender email (from)</Label>
              <Input
                id="sender_email"
                type="email"
                value={form.sender_email}
                onChange={(e) => update("sender_email", e.target.value)}
                className={inputCls}
                disabled={loading}
                data-testid="settings-input-sender-email"
              />
              <span className="text-[11px] text-brand-slate/60">
                Default <code>onboarding@resend.dev</code>. To use <code>hello@vanguardbeacon.com</code> you must verify the domain at resend.com/domains first.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sender_name" className="text-sm text-brand-navy">Sender display name</Label>
              <Input
                id="sender_name"
                value={form.sender_name}
                onChange={(e) => update("sender_name", e.target.value)}
                className={inputCls}
                disabled={loading}
                data-testid="settings-input-sender-name"
              />
            </div>

            <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
              <div className="text-[11px] text-brand-slate/60">
                {settings.updated_at && (
                  <>Last updated {formatDateTime(settings.updated_at)} by {settings.updated_by}</>
                )}
              </div>
              <button
                type="submit"
                disabled={saving || loading}
                className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60"
                data-testid="settings-save-button"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>

        {/* Social links card */}
        <section className="mt-6 bg-white border border-brand-navy/10 rounded-2xl p-6 md:p-8" data-testid="settings-social-card">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex w-10 h-10 items-center justify-center bg-brand-navy text-brand-orange rounded-full">
              <Share2 size={18} />
            </span>
            <div>
              <h2 className="font-semibold text-brand-navy">Social links</h2>
              <p className="text-xs text-brand-slate/70">
                Rendered as icons in the landing page footer. Leave a field empty to hide that icon.
              </p>
            </div>
          </div>

          <form onSubmit={onSaveSocial} className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="social_instagram" className="text-sm text-brand-navy flex items-center gap-2">
                <Instagram size={14} /> Instagram URL
              </Label>
              <Input
                id="social_instagram"
                type="url"
                value={socialForm.social_instagram}
                onChange={(e) => updateSocial("social_instagram", e.target.value)}
                placeholder="https://instagram.com/yourhandle"
                className={inputCls}
                disabled={loading}
                data-testid="settings-input-instagram"
              />
            </div>
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="social_linkedin" className="text-sm text-brand-navy flex items-center gap-2">
                <Linkedin size={14} /> LinkedIn URL
              </Label>
              <Input
                id="social_linkedin"
                type="url"
                value={socialForm.social_linkedin}
                onChange={(e) => updateSocial("social_linkedin", e.target.value)}
                placeholder="https://linkedin.com/company/vanguardbeacon"
                className={inputCls}
                disabled={loading}
                data-testid="settings-input-linkedin"
              />
            </div>
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="social_facebook" className="text-sm text-brand-navy flex items-center gap-2">
                <Facebook size={14} /> Facebook URL
              </Label>
              <Input
                id="social_facebook"
                type="url"
                value={socialForm.social_facebook}
                onChange={(e) => updateSocial("social_facebook", e.target.value)}
                placeholder="https://facebook.com/vanguardbeacon"
                className={inputCls}
                disabled={loading}
                data-testid="settings-input-facebook"
              />
            </div>

            <div className="sm:col-span-2 flex items-center justify-end pt-2">
              <button
                type="submit"
                disabled={savingSocial || loading}
                className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60"
                data-testid="settings-save-social-button"
              >
                <Save size={14} /> {savingSocial ? "Saving…" : "Save social links"}
              </button>
            </div>
          </form>
        </section>

        {/* Founder photos card */}
        <section className="mt-6 bg-white border border-brand-navy/10 rounded-2xl p-6 md:p-8" data-testid="settings-founders-card">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex w-10 h-10 items-center justify-center bg-brand-navy text-brand-orange rounded-full">
              <Users size={18} />
            </span>
            <div>
              <h2 className="font-semibold text-brand-navy">Founder photos</h2>
              <p className="text-xs text-brand-slate/70">
                Shown on the public <a href="/about" target="_blank" rel="noreferrer" className="text-brand-orange hover:underline">About Us</a> page.
                Recommended: portrait (4:5), at least 800×1000px. JPG, PNG or WEBP up to 8 MB.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <FounderPhotoField
              who="john"
              name="John Ong"
              role="Co-Founder"
              photoUrl={settings.founder_john_photo_url}
              uploading={uploadingFounder === "john"}
              onChange={(e) => onFounderPhotoChange("john", e)}
            />
            <FounderPhotoField
              who="chris"
              name="Chris Chen"
              role="Co-Founder"
              photoUrl={settings.founder_chris_photo_url}
              uploading={uploadingFounder === "chris"}
              onChange={(e) => onFounderPhotoChange("chris", e)}
            />
          </div>
        </section>

        {/* Placeholder for future settings */}
        <PipelineFiltersCard />

        <section className="mt-6 bg-white border border-brand-navy/10 rounded-2xl p-6 md:p-8 opacity-60" data-testid="settings-future-card">
          <h2 className="font-semibold text-brand-navy">Coming soon</h2>
          <p className="mt-2 text-sm text-brand-slate/75">
            Branded sender domain · Slack notifications · CRM forwarding · Custom reply templates · Marketing UTM attribution
          </p>
        </section>
      </main>
    </div>
  );
}

function FounderPhotoField({ who, name, role, photoUrl, uploading, onChange }) {
  const testid = `founder-photo-${who}`;
  return (
    <div className="border border-brand-navy/10 rounded-2xl p-4 md:p-5 bg-brand-cream/40" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="relative w-24 sm:w-28 aspect-[4/5] shrink-0 rounded-xl overflow-hidden bg-white border border-brand-navy/10">
          {photoUrl ? (
            <img
              src={absolutePhotoUrl(photoUrl)}
              alt={`${name} — ${role}`}
              className="w-full h-full object-cover"
              data-testid={`${testid}-preview`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[9px] uppercase tracking-[0.2em] text-brand-slate/50 text-center px-2">
              No photo
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-semibold">{role}</p>
          <h3 className="text-base font-semibold text-brand-navy">{name}</h3>
          <label
            className={`mt-4 inline-flex items-center gap-2 rounded-full border border-brand-navy/20 bg-white px-4 py-2 text-xs font-semibold text-brand-navy cursor-pointer hover:bg-brand-cream transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
            data-testid={`${testid}-upload-label`}
          >
            <Upload size={12} />
            {uploading ? "Uploading…" : (photoUrl ? "Replace photo" : "Upload photo")}
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,image/*"
              className="hidden"
              onChange={onChange}
              data-testid={`${testid}-input`}
            />
          </label>
        </div>
      </div>
    </div>
  );
}
